import { useEffect, useRef, useState } from "react";
import {
  Facebook,
  Instagram,
  Linkedin,
  MapPin,
  MessageCircle,
  Phone,
  Mail,
} from "lucide-react";
import styles from "./Footer.module.css";

const API_BASE_URL = process.env.REACT_APP_API || "http://localhost:5000";
const HEALTH_ENDPOINT = `${API_BASE_URL}/api/Conexao`;
const CHECK_INTERVAL = 30000;
const TIMEOUT_MS = 5000;

// 1. Defina o tipo union para os status possíveis
type ApiStatus = "checking" | "online" | "offline";

// 2. Tipar o objeto de configuração com Record<ApiStatus, ...>
const STATUS_CONFIG: Record<ApiStatus, { label: string; className: string }> = {
  checking: { label: "Verificando sistema...", className: "statusChecking" },
  online: { label: "Sistema operando", className: "statusOnline" },
  offline: { label: "Sistema Offline", className: "statusOffline" },
};

function Footer() {
  // 3. Tipar explicitamente o estado com o union type
  const [apiStatus, setApiStatus] = useState<ApiStatus>("checking");
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    async function checkApiHealth() {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

      try {
        const response = await fetch(HEALTH_ENDPOINT, {
          method: "GET",
          signal: controller.signal,
        });

        if (isMountedRef.current) {
          setApiStatus(response.ok ? "online" : "offline");
        }
      } catch (err) {
        if (isMountedRef.current) {
          setApiStatus("offline");
        }
      } finally {
        clearTimeout(timeoutId);
      }
    }

    checkApiHealth();
    const intervalId = setInterval(checkApiHealth, CHECK_INTERVAL);

    return () => {
      isMountedRef.current = false;
      clearInterval(intervalId);
    };
  }, []);

  const { label, className } = STATUS_CONFIG[apiStatus];

  return (
    <footer className={styles.footer}>
      <div className={styles.grid}>
        <div className={styles.brandCol}>
          <h5 className={styles.brandName}>Íkone Automação e Sistemas</h5>
          <p className={styles.brandTagline}>
            Soluções consistentes e duradouras em automação, eletromecânica e TI
            — da análise à startup.
          </p>
          <span className={`${styles.status} ${styles[className]}`}>
            {label}
          </span>
        </div>

        <div>
          <h6 className={styles.colTitle}>Links</h6>
          <ul className={styles.linkList}>
            <li>
              <a href="/" className={styles.link}>
                Início
              </a>
            </li>
            <li>
              <a href="#produtos" className={styles.link}>
                Produtos
              </a>
            </li>
            <li>
              <a href="#contato" className={styles.link}>
                Contato
              </a>
            </li>
          </ul>
        </div>

        <div>
          <h6 className={styles.colTitle}>Contato</h6>

          <ul className={styles.contactList}>
            <li className={styles.contactItem}>
              <MapPin size={16} className={styles.contactIcon} />
              Rua Nossa Senhora Mãe dos Homens, 678, Sala 1, Guarulhos – São
              Paulo
            </li>
            <li className={styles.contactItem}>
              <Mail size={16} className={styles.contactIcon} />
              atendimento@ikoneautomacao.com
            </li>
          </ul>

          <h6 className={styles.colTitle}>Escritório</h6>
          <ul className={styles.contactList}>
            <li className={styles.contactItem}>
              <Phone size={16} className={styles.contactIcon} />
              (11) 94509-4273
            </li>
          </ul>

          <h6 className={styles.colTitle}>Comercial</h6>
          <ul className={styles.contactList}>
            <li className={styles.contactItem}>
              <Phone size={16} className={styles.contactIcon} />
              (11) 96337-0510
            </li>
            <li className={styles.contactItem}>
              <Phone size={16} className={styles.contactIcon} />
              (11) 98145-8600
            </li>
          </ul>
        </div>

        <div>
          <h6 className={styles.colTitle}>Redes Sociais</h6>
          <div className={styles.socialRow}>
            <a href="/" className={styles.socialLink} aria-label="Facebook">
              <Facebook size={18} />
            </a>
            <a href="/" className={styles.socialLink} aria-label="Instagram">
              <Instagram size={18} />
            </a>
            <a
              href="https://br.linkedin.com/company/%C3%ADkone-automa%C3%A7%C3%A3o-e-sistemas-ltda"
              className={styles.socialLink}
              rel="noopener noreferrer"
              target="_blank"
              aria-label="LinkedIn"
            >
              <Linkedin size={18} />
            </a>
            <a href="/" className={styles.socialLink} aria-label="WhatsApp">
              <MessageCircle size={18} />
            </a>
          </div>
        </div>
      </div>

      <div className={styles.bottomBar}>
        <p className={styles.copyright}>
          © {new Date().getFullYear()} Íkone Automação e Sistemas Ltda. Todos os
          direitos reservados.
        </p>
      </div>
    </footer>
  );
}

export default Footer;
